/**
 * Renders the video variant that matches the current viewport.
 *
 * Both variants are authored as `<template data-viewport="desktop|mobile">` children, so the
 * variant that is not shown never enters the DOM and its video is never downloaded. The variant
 * is swapped when the viewport crosses the breakpoint, which also stops playback of the old one.
 */
const MOBILE_MEDIA_QUERY = '(max-width: 749px)';
const RENDERED_ATTRIBUTE = 'data-responsive-video-rendered';

class ResponsiveVideo extends HTMLElement {
  /** @type {MediaQueryList} */
  #mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY);

  /** @type {string | null} */
  #rendered = null;

  #handleChange = () => this.#render();

  connectedCallback() {
    this.#mobileQuery.addEventListener('change', this.#handleChange);
    this.#render();
  }

  disconnectedCallback() {
    this.#mobileQuery.removeEventListener('change', this.#handleChange);
  }

  #render() {
    const viewport = this.#mobileQuery.matches ? 'mobile' : 'desktop';
    const isRendered = this.querySelector(`[${RENDERED_ATTRIBUTE}]`) != null;
    if (isRendered && viewport === this.#rendered) return;

    const template =
      this.querySelector(`template[data-viewport="${viewport}"]`) ??
      this.querySelector('template[data-viewport="desktop"]');

    if (!(template instanceof HTMLTemplateElement)) return;

    for (const previous of this.querySelectorAll(`[${RENDERED_ATTRIBUTE}]`)) {
      previous.remove();
    }

    const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));

    for (const element of fragment.children) {
      element.setAttribute(RENDERED_ATTRIBUTE, viewport);
    }

    this.appendChild(fragment);
    this.#rendered = viewport;
    this.toggleAttribute('rendered', true);
  }
}

if (!customElements.get('responsive-video')) {
  customElements.define('responsive-video', ResponsiveVideo);
}
